import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/router"

import { Progress } from "@coval/ui"

// A navigation that settles inside this window never shows the bar, so cached and instant transitions do not flash it.
const SHOW_DELAY_MS = 150
const INITIAL_VALUE = 10
const TRICKLE_INTERVAL_MS = 300
// The Pages Router reports no real progress, so the bar eases toward this ceiling and only fills when the navigation settles.
const TRICKLE_CEILING = 90
const TRICKLE_RATE = 0.1
// Long enough for the primitive's width transition to finish before the bar unmounts.
const COMPLETE_HIDE_DELAY_MS = 300
// A navigation guard that cancels by throwing from `routeChangeStart` without reporting a `routeChangeError` leaves the router emitting neither completion nor error, so the bar gives up rather than trickle forever.
const MAX_PENDING_MS = 20_000

interface RouteChangeOptions {
  shallow?: boolean
}

function isCancellation(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { cancelled?: unknown }).cancelled === true
  )
}

/**
 * Shows a thin progress bar at the top of the viewport while a client-side
 * route change is pending.
 */
export const GlobalNavigationIndicator = () => {
  const router = useRouter()
  const [value, setValue] = useState<number | null>(null)
  const visibleRef = useRef(false)

  useEffect(() => {
    let showTimeout: number | undefined
    let trickleInterval: number | undefined
    let hideTimeout: number | undefined
    let abandonTimeout: number | undefined
    let activeUrl: string | undefined

    const clearTimers = () => {
      window.clearTimeout(showTimeout)
      window.clearInterval(trickleInterval)
      window.clearTimeout(hideTimeout)
      window.clearTimeout(abandonTimeout)
    }

    const handleStart = (url: string, options?: RouteChangeOptions) => {
      if (options?.shallow) {
        return
      }
      activeUrl = url
      clearTimers()
      // A navigation that starts while the previous one's completed bar is fading out begins hidden, so it gets its own show delay rather than inheriting a full bar.
      visibleRef.current = false
      setValue(null)
      showTimeout = window.setTimeout(() => {
        visibleRef.current = true
        setValue(INITIAL_VALUE)
        trickleInterval = window.setInterval(() => {
          setValue((current) =>
            current === null
              ? current
              : current + (TRICKLE_CEILING - current) * TRICKLE_RATE,
          )
        }, TRICKLE_INTERVAL_MS)
      }, SHOW_DELAY_MS)
      abandonTimeout = window.setTimeout(settle, MAX_PENDING_MS)
    }

    const settle = () => {
      clearTimers()
      if (!visibleRef.current) {
        return
      }
      setValue(100)
      hideTimeout = window.setTimeout(() => {
        visibleRef.current = false
        setValue(null)
      }, COMPLETE_HIDE_DELAY_MS)
    }

    const handleComplete = (_url: string, options?: RouteChangeOptions) => {
      if (options?.shallow) {
        return
      }
      settle()
    }

    const handleError = (error: unknown, url: string) => {
      // A superseded navigation can report its cancellation after the replacement has started; only a cancellation of the navigation the bar is showing, such as an unsaved-changes guard blocking it, settles the bar.
      if (isCancellation(error) && url !== activeUrl) {
        return
      }
      settle()
    }

    router.events.on("routeChangeStart", handleStart)
    router.events.on("routeChangeComplete", handleComplete)
    router.events.on("routeChangeError", handleError)
    return () => {
      clearTimers()
      router.events.off("routeChangeStart", handleStart)
      router.events.off("routeChangeComplete", handleComplete)
      router.events.off("routeChangeError", handleError)
    }
  }, [router.events])

  if (value === null) {
    return null
  }

  return (
    <Progress
      aria-label="Loading page"
      value={value}
      className="pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5 rounded-none bg-transparent"
    />
  )
}
